import { StackedSheet } from "@/components/ui/stacked-sheet";
import { useState, type ReactNode } from "react";
import { VisitTaskFoot } from "@/components/ui/visit-task-foot";

import { visitCopy } from "./copy";

interface VisitFrameProps {
  /** The trail as written; the frame's `data-visit`. */
  trail: readonly string[];
  /** The way back: the origin's name, then each earlier level's. */
  names: readonly string[];
  heading: string;
  headingId: string;
  /** The address is refused: one sentence and the way back, naming nothing (R1). */
  refused?: boolean;
  busy?: boolean;
  /** Return to a level: 0 is the origin. */
  onStep(to: number): void;
  children?: ReactNode;
  activeBoxIds?: string[];
  dockFooter?: boolean;
}

const STEP = "inline-flex min-h-[var(--control-height)] items-center text-ui font-semibold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

/**
 * The visit frame (O10, CASE-027): not a box. It takes its origin box's place,
 * names the way back once and its subject once, and holds the visited boxes as
 * siblings in one column.
 */
export function VisitFrame({ trail, names, heading, headingId, refused, busy, onStep, children, activeBoxIds = [], dockFooter = false }: VisitFrameProps) {
  // Refused, the way back, the heading and the sentence are the kit's page refusal in the origin's
  // place, so the judge reads them as one (CASE-022). A refused address names nothing: the person may
  // not see it or it doesn't exist (R1). The earlier levels' kept boxes stay outside it.
  const [footTarget, setFootTarget] = useState<HTMLDivElement | null>(null);
  const refusal = refused ? { "data-bento-page": `visit:${trail.join(",")}`, "data-journey": "visit", "data-page-refusal": "forbidden" } : {};
  return (
    <VisitTaskFoot.Provider value={{ target: footTarget, activeBoxIds: new Set(activeBoxIds) }}>
    <StackedSheet
      open
      footer={dockFooter ? <div ref={setFootTarget} data-visit-actions="" className="w-full [container-type:inline-size]" /> : undefined}
      onBack={() => onStep(trail.length > 1 ? trail.length - 1 : 0)}
      title={heading}
      titleId={headingId}
      returnLabel={trail.length > 1 ? "Back" : visitCopy.back(names[0] ?? "")}
      backButton={trail.length > 1}
      contentProps={{
        onOpenAutoFocus: (event) => event.preventDefault(),
        onCloseAutoFocus: (event) => event.preventDefault(),
        // Radix observes document Escape before React's saved-field handler.
        // The field owns this key: restore its value while keeping the visit.
        onEscapeKeyDown: (event) => {
          const target = event.target;
          if (target instanceof Element && target.matches("input,select,textarea,[contenteditable=true]") && target.closest("[data-field]")) event.preventDefault();
        },
      }}
      sectionProps={{
        "data-visit": trail.join(","),
        "data-visit-refusal": refused ? "" : undefined,
        "aria-labelledby": headingId,
        "aria-busy": busy || undefined,
      }}
      headerProps={refusal}
      headerAfter={refused ? <p className="text-ui text-foreground-soft">{visitCopy.refusedSentence}</p> : undefined}
      headerContext={trail.length > 1 ? (
        <nav data-trail="" aria-label={visitCopy.trail}>
          <ol className="flex flex-wrap items-center text-ui text-foreground-soft">
            {names.map((name, level) => (
              <li key={trail[level] ?? "origin"} className="inline-flex items-center after:px-2 after:text-muted-foreground after:content-['›'] last:after:content-none">
                <button type="button" data-act="exit" data-return={level === names.length - 1 ? "" : undefined} className={STEP} onClick={() => onStep(level)}>{name}</button>
              </li>
            ))}
          </ol>
        </nav>
      ) : undefined}
    >
      {children}
    </StackedSheet>
    </VisitTaskFoot.Provider>
  );
}
